import Box from "@mui/material/Box";
import { useCallback, useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useTranslation } from "react-i18next";
import { SelectChangeEvent } from "@mui/material";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useNavigate } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Payment } from "assets/SideBarIcons/money-transfer.svg";
import BackendPaginationTable from "common/Table/TableWithPagination";
import PurchasePaymentSearchHeader from "components/Inventory/PurchasePaymentSearchHeader";
import Loader from "layout/Loader";
import { useGetPurchasePaymentService } from "subModule/src/services/inventory/useInventory";

interface SearchFormData {
  [key: string]: string | any;
}
interface PaymentProps {
  supplierId?: number;
}
export default function PurchasePaymentListing(props: PaymentProps) {
  const navigate = useNavigate();
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(props?.supplierId ? 5 : 20);
  const [loading, setLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<string[]>(["paymentNo"]);
  const [search, setSearch] = useState<any>({});
  const [searchFormData, setSearchFormData] = useState<SearchFormData>({
    paymentNo: "",
    referenceNo: "",
    supplierName: "",
    start: null,
    end: null,
  });
  const {
    data: tableData,
    isFetching,
    refetch,
  } = useGetPurchasePaymentService({
    pageNumber: pageNo,
    size: pageSize,
    ...search,
    ...(props?.supplierId && { supplierId: props.supplierId }),
  });
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const fetchData = useCallback(() => {
    refetch()
      .then((res: any) => {
        if (!res?.data?.pages[0]?.success)
          enqueueSnackbar(
            <AlertMessages
              text={res?.data?.pages[0]?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    fetchData();
  }, [pageNo, pageSize, search]);
  useEffect(() => {
    if (loading) handleSubmitSearch();
  }, [searchFormData.start, searchFormData.end]);
  const handleChangePage = (pageNo: number) => {
    setPageNo(pageNo + 1);
  };
  const handleChangeRows = (pageSize: number) => {
    setPageSize(pageSize);
    setPageNo(1);
  };
  const showRowView = (row: any) =>
    navigate("/inventory/supplier-payment/" + row.id + "/print", {
      state: { supplier: props?.supplierId || null },
    });
  const clearSearch = () => {
    setFilter(["paymentNo"]);
    setSearchFormData({
      paymentNo: "",
      referenceNo: "",
      supplierName: "",
      start: null,
      end: null,
    });
    if (Object.keys(search).length) {
      setSearch({});
    }
  };
  const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
    const {
      target: { value },
    } = event;
    setFilter(typeof value === "string" ? value.split(",") : value);
    setSearchFormData((prevFormData) => {
      const selectedFilters =
        typeof value === "string" ? value.split(",") : value;
      const updatedFormData = Object.fromEntries(
        Object.keys(prevFormData).map((key) => [
          key,
          value.includes(key) ? prevFormData[key] : "",
        ]),
      );
      if (selectedFilters.includes("dateInterval")) {
        updatedFormData.start = prevFormData.start;
        updatedFormData.end = prevFormData.end;
      }
      return updatedFormData;
    });
    if (!value.length && Object.keys(search).length) {
      clearSearch();
    }
  };
  const handleChangeSearch = (field: string, value: any) => {
    const val = value;
    setSearchFormData((prevData) => ({
      ...prevData,
      start: value[0],
      end: value[1],
    }));
    if (field === "dateInterval") {
      if ((value[0] && value[1]) || (value[0] === null && value[1] === null)) {
        setLoading(true);
      }
    } else {
      setSearchFormData((prevData) => ({
        ...prevData,
        [field]: val,
      }));
    }
  };
  const handleSubmitSearch = () => {
    const filteredSearch: Record<string, string> = {};
    const { start, end, ...rest } = searchFormData;
    if (start && end) {
      filteredSearch.start = start;
      filteredSearch.end = end;
    } else {
      // ❗ If one or both missing → remove both
      setSearchFormData((prev) => ({
        ...prev,
        start: null,
        end: null,
      }));
    }
    Object.entries(rest).forEach(([key, value]) => {
      if (value) {
        filteredSearch[key] = value?.trim();
      }
    });
    setSearch(filteredSearch);
    setPageNo(1);
    setPageSize(pageSize);
  };
  return (
    <Box>
      {props?.supplierId ? null : (
        <ComponentHeaderTwo
          showBtn={true}
          showSaveBtn={false}
          onClick={() => navigate("/inventory/new-supplier-payment")}
          heading={t("supplierPayments")}
          icon={<Payment height={28} width={28} />}
          btnText={"create"}
          btnType={"create"}
          showCreateBtn={checkPrivileges("ROLE_PURCHASE_PAYMENT_CREATE")}
        />
      )}
      {isFetching || loading ? (
        <Loader />
      ) : (
        <BackendPaginationTable
          columns={[
            {
              id: "paymentNumber",
              label: "Payment Number",
              numeric: false,
              width: "14%",
            },
            {
              id: "referenceNumber",
              label: "referenceNo",
              numeric: false,
              width: "12%",
            },
            {
              id: "paymentModeTitle",
              label: "PaymentMode",
              numeric: false,
              width: "12%",
            },
            {
              id: "supplierName",
              label: "vendor",
              numeric: false,
              width: "16%",
            },
            {
              id: "balanceAmount",
              label: "extraAmount",
              numeric: false,
              width: "12%",
            },
            { id: "amount", label: "amount", numeric: false, width: "10%" },
            {
              id: "paymentDate",
              label: "paymentDate",
              numeric: false,
              width: "12%",
            },
            {
              id: "adjustmentType",
              label: "adjustmentType",
              numeric: false,
              width: "12%",
            },
          ]}
          data={tableData?.pages[0]?.data?.content || []}
          pageNo={pageNo}
          pageSize={pageSize}
          tableData={tableData?.pages[0]?.data || {}}
          showCheckbox={false}
          showActions={true}
          enableSorting={true}
          showHeader={true}
          showSearch={true}
          showFilter={true}
          showCreateButton={true}
          handleChangePage={handleChangePage}
          handleChangeRows={handleChangeRows}
          rowClick={checkPrivileges("ROLE_PURCHASE_PAYMENT_UPDATE")}
          showRowView={showRowView}
          loadingSearch={false}
          headerComponent={
            <Box className="table-header-search">
              <PurchasePaymentSearchHeader
                searchFormData={searchFormData}
                handleChangeFilter={handleChangeFilter}
                handleChangeSearch={handleChangeSearch}
                handleSubmitSearch={handleSubmitSearch}
                clearSearch={clearSearch}
                supplier={props?.supplierId}
                {...{ filter, search }}
              />
            </Box>
          }
        />
      )}
    </Box>
  );
}
